/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { cva, type VariantProps } from 'class-variance-authority'
import type { ComponentProps, ReactNode } from 'react'

import { cn } from '@/lib/utils'

const labeledDividerVariants = cva('flex items-center gap-3 px-2', {
  variants: {
    variant: {
      default: 'py-3 text-muted-foreground',
      destructive: 'py-2 font-medium text-destructive',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const labeledDividerRuleVariants = cva('h-px flex-1', {
  variants: {
    variant: {
      default: 'bg-border',
      destructive: 'bg-destructive',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

export interface LabeledDividerProps extends ComponentProps<'div'>, VariantProps<typeof labeledDividerVariants> {
  children: ReactNode
}

export function LabeledDivider({ children, className, variant = 'default', ...props }: LabeledDividerProps) {
  return (
    <div className={cn(labeledDividerVariants({ variant }), className)} data-slot="labeled-divider" {...props}>
      <span aria-hidden className={labeledDividerRuleVariants({ variant })} />
      <span className="text-sm">{children}</span>
      <span aria-hidden className={labeledDividerRuleVariants({ variant })} />
    </div>
  )
}
